import { Decimal } from 'decimal.js';
import { FxRateMissingError } from './errors';
import { toDecimal } from './money';
import type { CurrencyCode, FxRate } from './types';

/** สร้าง document ID ของคู่เงิน เช่น ('USD','THB') → 'USDTHB' (spec Section 4.6) */
export function fxPairId(from: CurrencyCode, to: CurrencyCode): string {
  return `${from.toUpperCase()}${to.toUpperCase()}`;
}

/**
 * หาอัตราแลกเปลี่ยน from → to
 *
 * รองรับ 3 กรณี: สกุลเดียวกัน (= 1), มีคู่ตรง, มีคู่กลับ (ใช้ส่วนกลับ)
 * ไม่รองรับการต่อทอดผ่านสกุลที่ 3 (เช่น EUR→USD→THB) เพราะ MVP มีแค่ USD/THB —
 * ถ้าอนาคตถือสินทรัพย์สกุลอื่นค่อยเพิ่ม แล้วต้องเพิ่ม test ด้วย
 *
 * คืน `null` เมื่อหาไม่เจอ — ผู้เรียกตัดสินใจเองว่าจะ throw หรือแสดง "—"
 */
export function tryResolveFxRate(
  from: CurrencyCode,
  to: CurrencyCode,
  rates: ReadonlyMap<string, FxRate>,
): Decimal | null {
  const f = from.toUpperCase();
  const t = to.toUpperCase();

  if (f === t) {
    return new Decimal(1);
  }

  const direct = rates.get(fxPairId(f, t));
  if (direct) {
    return toDecimal(direct.rate, `fx.${fxPairId(f, t)}.rate`);
  }

  const inverse = rates.get(fxPairId(t, f));
  if (inverse) {
    const rate = toDecimal(inverse.rate, `fx.${fxPairId(t, f)}.rate`);
    // อัตรา 0 หารกลับไม่ได้ — ถือว่าเป็นข้อมูลเสีย ไม่ใช่ "แปลงได้เท่ากับ 0"
    if (rate.isZero()) {
      return null;
    }
    return new Decimal(1).div(rate);
  }

  return null;
}

/** เหมือน tryResolveFxRate แต่ throw แทนการคืน null — ใช้ตอนที่ขาดไม่ได้จริง ๆ */
export function resolveFxRate(
  from: CurrencyCode,
  to: CurrencyCode,
  rates: ReadonlyMap<string, FxRate>,
): Decimal {
  const rate = tryResolveFxRate(from, to, rates);
  if (rate === null) {
    throw new FxRateMissingError(from, to);
  }
  return rate;
}
